/**
 * basketCandles — one candlestick series for a whole option basket, built from each leg's
 * own candles. Pure (no network, no React); `scripts/verify-basket-candles.mjs` pins it.
 *
 * WHY MARK CANDLES. A thin option strike trades a few times a day, so its TRADE candles are
 * flat or missing for hours. Delta also publishes MARK-price candles ("MARK:C-BTC-…"),
 * which exist for every bar. The basket chart uses them by default.
 *
 * OPEN / CLOSE are exact: the basket's value is a fixed linear sum of the legs' prices.
 * HIGH / LOW are not knowable exactly from bars (legs peak at different moments). Each FINE
 * bar takes the bound Σ max(w·h, w·l) / Σ min(w·h, w·l); fine bars are then resampled to the
 * chart interval. On short fine bars the bound is tight; the coarse bar's extremes then come
 * from the real path through the fine bars.
 *
 * VALUE. V(t) = Σ side·lots·price(t) with SELL = +1, BUY = −1 (the net credit to open, Delta
 * units: USD per 1 base unit). Views:
 *   premium — in real USD of the position (V × contractValue):
 *             credit basket (entry V > 0): what closing would cost; falling = profit.
 *             debit basket: −V × cv = what the position is worth; rising = profit.
 *   pnl     — (V_entry − V(t)) × contractValue × fx: rising = profit, 0 = break-even vs entry.
 */

export interface BcCandle { time: number; open: number; high: number; low: number; close: number }
export interface BcLeg { action: 'BUY' | 'SELL'; lots: number; entry: number }
export type BcView = 'premium' | 'pnl';

/** Entry net credit of the legs (V at the entry prices). */
export const entryValue = (legs: BcLeg[]): number => legs.reduce((s, l) => s + (l.action === 'SELL' ? 1 : -1) * l.lots * l.entry, 0);

/** Sign that turns V into the displayed value for the chosen view. */
export function viewMap(legs: BcLeg[], view: BcView, cv: number, fx: number): (v: number) => number {
  const v0 = entryValue(legs);
  if (view === 'pnl') return v => (v0 - v) * cv * fx;
  return v0 >= 0 ? v => v * cv : v => -v * cv;
}

/**
 * Combine per-leg candle series (same resolution) into the basket's V candles.
 * Union time grid; a leg missing a bar contributes flat at its last close (forward-fill);
 * bars before every leg has started are skipped.
 */
export function combineLegs(legs: BcLeg[], series: BcCandle[][]): BcCandle[] {
  if (!legs.length || series.length !== legs.length || series.some(s => !s.length)) return [];
  const w = legs.map(l => (l.action === 'SELL' ? 1 : -1) * l.lots);
  const maps = series.map(arr => { const m = new Map<number, BcCandle>(); for (const c of arr) m.set(c.time, c); return m; });
  const tset = new Set<number>();
  for (const arr of series) for (const c of arr) tset.add(c.time);
  const times = Array.from(tset).sort((a, b) => a - b);
  const last: (BcCandle | null)[] = series.map(() => null);
  const out: BcCandle[] = [];
  for (const t of times) {
    for (let i = 0; i < maps.length; i++) { const c = maps[i].get(t); if (c) last[i] = c; }
    if (last.some(c => !c)) continue;
    let o = 0, cl = 0, hi = 0, lo = 0;
    for (let i = 0; i < maps.length; i++) {
      const real = maps[i].get(t);
      const p = last[i]!;
      const c = real || { time: t, open: p.close, high: p.close, low: p.close, close: p.close };
      o += w[i] * c.open; cl += w[i] * c.close;
      hi += Math.max(w[i] * c.high, w[i] * c.low);
      lo += Math.min(w[i] * c.high, w[i] * c.low);
    }
    out.push({ time: t, open: o, close: cl, high: Math.max(hi, o, cl), low: Math.min(lo, o, cl) });
  }
  return out;
}

/** Apply a monotone-or-decreasing map to candles (a decreasing map swaps high and low). */
export function mapCandles(cs: BcCandle[], f: (v: number) => number): BcCandle[] {
  return cs.map(c => {
    const a = f(c.high), b = f(c.low), o = f(c.open), cl = f(c.close);
    return { time: c.time, open: o, close: cl, high: Math.max(a, b, o, cl), low: Math.min(a, b, o, cl) };
  });
}

/**
 * Resample to `periodSec` buckets. Times are the chart's IST-shifted seconds (UTC + 19800),
 * so buckets align on UTC boundaries like Delta's own bars (a daily bar starts 05:30 IST).
 */
export function resample(cs: BcCandle[], periodSec: number, shiftSec = 19800): BcCandle[] {
  if (!cs.length) return [];
  const out: BcCandle[] = [];
  let cur: BcCandle | null = null;
  for (const c of cs) {
    const b = Math.floor((c.time - shiftSec) / periodSec) * periodSec + shiftSec;
    if (!cur || cur.time !== b) {
      if (cur) out.push(cur);
      cur = { time: b, open: c.open, high: c.high, low: c.low, close: c.close };
    } else {
      cur.high = Math.max(cur.high, c.high); cur.low = Math.min(cur.low, c.low); cur.close = c.close;
    }
  }
  if (cur) out.push(cur);
  return out;
}

/** Fold the live value into the newest bar (or start the next one when a new bucket began). */
export function withLive(cs: BcCandle[], v: number | null, nowSec: number, periodSec: number, shiftSec = 19800): BcCandle[] {
  if (v == null || !Number.isFinite(v) || !cs.length) return cs;
  const b = Math.floor((nowSec - shiftSec) / periodSec) * periodSec + shiftSec;
  const last = cs[cs.length - 1];
  if (last.time === b) {
    return cs.slice(0, -1).concat({ ...last, close: v, high: Math.max(last.high, v), low: Math.min(last.low, v) });
  }
  if (b > last.time) return cs.concat({ time: b, open: last.close, close: v, high: Math.max(last.close, v), low: Math.min(last.close, v) });
  return cs;
}

/** The finer resolution fetched for a chart interval (minutes). */
export function fineFor(intervalMin: number): number {
  if (intervalMin <= 5) return intervalMin;
  if (intervalMin <= 15) return 5;
  if (intervalMin <= 60) return 15;
  if (intervalMin <= 240) return 60;
  return 240;
}

// ── Exit lines (target / stop) on the basket chart — 2026-11-02 ─────────────────────────
//
// The basket's exits live in one of two units, and both are drawn on either view by first
// converting them to V (the net-to-close, Delta units) and then through viewMap:
//   FIXED   — ₹ on TOTAL P&L = adjustments already booked + the open legs. The chart's
//             P&L view shows the open legs only, so a target of +T sits at chart P&L
//             T − booked, and a stop of −S at −S − booked.
//   PREMIUM — targetPremium / slPremium ARE net-to-close values (V).
// A dragged line goes the other way: chart value → V (inverseView) → ₹ or premium.
//
// NET LEVELS (2026-11-03). Every target / stop is NET — what the owner receives after the
// exit slippage (closing at the bid / ask) and both sides' fees. The chart's candles are at
// the marks (gross), so a line is drawn where the GROSS P&L = level + costInr: that is where
// the monitor fires it. costInr comes from the monitor's live state (gross − net, ₹).

export interface ExitBasket {
  exitMode?: string | null;
  tpAmountInr?: number | string | null;
  slAmountInr?: number | string | null;
  targetPremium?: number | string | null;
  slPremium?: number | string | null;
  adjustmentsRealizedPnl?: number | string | null;
}
export interface ExitLevelsV { mode: 'FIXED' | 'PREMIUM'; tpV: number; slV: number; booked: number; costInr: number }

const pos = (x: any): number | null => { const n = Number(x); return x != null && Number.isFinite(n) && n > 0 ? n : null; };
const fin = (x: any): number | null => { const n = Number(x); return x != null && x !== '' && Number.isFinite(n) ? n : null; };

/** The basket's target and stop as V levels; null when it has none to draw. */
export function exitLevelsV(b: ExitBasket, legs: BcLeg[], cv: number, fx: number, costInr = 0): ExitLevelsV | null {
  const v0 = entryValue(legs);
  const k = cv * fx;
  const booked = Number(b.adjustmentsRealizedPnl) || 0;
  const cost = Number.isFinite(costInr) && costInr > 0 ? costInr : 0;
  if (b.exitMode === 'FIXED') {
    // slAmountInr is SIGNED: +X = stop at a ₹X loss, −X = stop at a ₹X profit (profit floor).
    const tp = pos(b.tpAmountInr), sl = fin(b.slAmountInr);
    if (tp == null || sl == null || sl === 0 || !(k > 0)) return null;
    return { mode: 'FIXED', tpV: v0 - (tp - booked + cost) / k, slV: v0 - (-sl - booked + cost) / k, booked, costInr: cost };
  }
  // Premium levels are compared with the net-to-close AFTER costs (= mark V + cost / k).
  const t = fin(b.targetPremium), s = fin(b.slPremium);
  if (t == null || s == null) return null;
  const dv = k > 0 ? cost / k : 0;
  return { mode: 'PREMIUM', tpV: t - dv, slV: s - dv, booked, costInr: cost };
}

/** Inverse of viewMap: a value read off the chart → V. */
export function inverseView(legs: BcLeg[], view: BcView, cv: number, fx: number): (y: number) => number {
  const v0 = entryValue(legs);
  if (view === 'pnl') return y => v0 - y / (cv * fx);
  return v0 >= 0 ? y => y / cv : y => -y / cv;
}

/**
 * What saving a dragged line means. FIXED: both ₹ amounts (the untouched one unchanged),
 * rounded to the rupee; PREMIUM: the moved premium only. `problem` is set when the new
 * level is not a valid exit (a target at or below a loss, a stop at or above a profit).
 */
export function exitSavePatch(lv: ExitLevelsV, which: 'tp' | 'sl', newV: number, legs: BcLeg[], cv: number, fx: number):
  { patch: Record<string, number | string>; totalInr: number | null; problem: string | null } {
  const v0 = entryValue(legs);
  const cost = lv.costInr || 0;
  if (lv.mode === 'FIXED') {
    const total = (v0 - newV) * cv * fx + lv.booked - cost;   // NET total P&L at that level
    const tp = which === 'tp' ? Math.round(total) : Math.round((v0 - lv.tpV) * cv * fx + lv.booked - cost);
    const sl = which === 'sl' ? Math.round(-total) : Math.round(-((v0 - lv.slV) * cv * fx + lv.booked - cost));
    // A stop may sit in PROFIT (a floor that locks a minimum gain) — it only has to stay
    // below the target. The server also refuses a stop the live P&L is already under.
    const problem = !(tp > 0) ? 'A target must be a profit (above ₹0 total P&L).'
      : -sl >= tp ? 'The stop must stay below the target.'
      : sl === 0 ? 'Move the stop off exactly ₹0.' : null;
    return { patch: { exitMode: 'FIXED', tpAmountInr: tp, slAmountInr: sl }, totalInr: Math.round(total), problem };
  }
  const v = +(newV + (cv * fx > 0 ? cost / (cv * fx) : 0)).toFixed(4);
  return { patch: which === 'tp' ? { targetPremium: v } : { slPremium: v }, totalInr: null, problem: null };
}

// ── Max profit / max loss at expiry (fixed reference lines) ──
// V(S) = Σ side·lots·intrinsic(S) at expiry; P&L = (V0 − V)·cv·fx, so MAX PROFIT is the
// smallest V and MAX LOSS the largest. V is piecewise-linear with kinks at the strikes:
// the extremes are at S = 0, a strike, or unbounded in the tail above the top strike.
export interface ExpLeg { action: 'BUY' | 'SELL'; lots: number; strike: number; optionType: 'CE' | 'PE' }
/** minV / maxV of the legs' expiry value; null = unbounded (unlimited profit / loss). */
export function expiryExtremesV(legs: ExpLeg[]): { minV: number | null; maxV: number | null } | null {
  if (!legs.length || legs.some(l => !(l.strike > 0) || !(l.lots > 0))) return null;
  const at = (S: number) => legs.reduce((a, l) => a + (l.action === 'SELL' ? 1 : -1) * l.lots
    * (l.optionType === 'CE' ? Math.max(0, S - l.strike) : Math.max(0, l.strike - S)), 0);
  const pts = [0, ...legs.map(l => l.strike)].map(at);
  const tail = legs.reduce((a, l) => a + (l.optionType === 'CE' ? (l.action === 'SELL' ? 1 : -1) * l.lots : 0), 0);
  return { minV: tail < 0 ? null : Math.min(...pts), maxV: tail > 0 ? null : Math.max(...pts) };
}
